import React, { useState, useEffect } from 'react';

const AdvanceTaxCalculator = () => {
  const [estimatedIncome, setEstimatedIncome] = useState('');
  const [estimatedTax, setEstimatedTax] = useState('');
  const [tdsDeducted, setTdsDeducted] = useState('');
  const [selfAssessmentTax, setSelfAssessmentTax] = useState('');
  
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (estimatedIncome && estimatedTax) {
      calculateAdvanceTax();
    } else {
      setResults(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedIncome, estimatedTax, tdsDeducted, selfAssessmentTax]);

  const calculateAdvanceTax = () => {
    const income = parseFloat(estimatedIncome) || 0;
    const totalTax = parseFloat(estimatedTax) || 0;
    const tds = parseFloat(tdsDeducted) || 0;
    const selfAssess = parseFloat(selfAssessmentTax) || 0;

    // Calculate advance tax liability
    const advanceTaxLiability = totalTax - tds - selfAssess;

    // Only if liability is above ₹10,000
    const isAdvanceTaxApplicable = advanceTaxLiability >= 10000;

    // Calculate installments (due dates and amounts)
    const installments = [
      { 
        quarter: 'Q1', 
        dueDate: 'June 15', 
        percentage: 15, 
        amount: (advanceTaxLiability * 0.15).toFixed(2),
        cumulativePercentage: 15,
      },
      { 
        quarter: 'Q2', 
        dueDate: 'September 15', 
        percentage: 30, 
        amount: (advanceTaxLiability * 0.30).toFixed(2),
        cumulativePercentage: 45,
      },
      { 
        quarter: 'Q3', 
        dueDate: 'December 15', 
        percentage: 30, 
        amount: (advanceTaxLiability * 0.30).toFixed(2),
        cumulativePercentage: 75,
      },
      { 
        quarter: 'Q4', 
        dueDate: 'March 15', 
        percentage: 25, 
        amount: (advanceTaxLiability * 0.25).toFixed(2),
        cumulativePercentage: 100,
      },
    ];

    // Calculate cumulative amounts
    let cumulative = 0;
    installments.forEach(inst => {
      cumulative += parseFloat(inst.amount);
      inst.cumulative = cumulative.toFixed(2);
    });

    // Calculate interest for non-payment (234B and 234C)
    const interest234B = calculateInterest234B(advanceTaxLiability);
    const interest234C = calculateInterest234C(advanceTaxLiability, installments);

    setResults({
      income: income.toFixed(2),
      totalTax: totalTax.toFixed(2),
      tdsDeducted: tds.toFixed(2),
      selfAssessmentTax: selfAssess.toFixed(2),
      advanceTaxLiability: advanceTaxLiability.toFixed(2),
      isApplicable: isAdvanceTaxApplicable,
      installments,
      interest234B: interest234B.toFixed(2),
      interest234C: interest234C.toFixed(2),
    });
  };

  const calculateInterest234B = (liability) => {
    // Interest if advance tax not paid or less than 90% of liability
    // 1% per month for 3 months (simplified calculation)
    return liability * 0.01 * 3;
  };

  const calculateInterest234C = (liability, installments) => {
    // Interest for deferment of installments
    // Simplified: 1% per month on shortfall
    let totalInterest = 0;
    
    // If Q1 not paid: 1% for 9 months
    totalInterest += (liability * 0.15) * 0.01 * 9;
    // If Q2 not paid: 1% for 6 months
    totalInterest += (liability * 0.30) * 0.01 * 6;
    // If Q3 not paid: 1% for 3 months
    totalInterest += (liability * 0.30) * 0.01 * 3;
    
    return totalInterest;
  };

  const copyToClipboard = () => {
    if (!results) return;
    
    let summary = `
📅 Advance Tax Payment Schedule
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Tax Liability: ₹${results.totalTax}
Less: TDS Deducted: ₹${results.tdsDeducted}
Advance Tax Payable: ₹${results.advanceTaxLiability}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Payment Schedule:
`;

    results.installments.forEach(inst => {
      summary += `\n${inst.quarter} - Due: ${inst.dueDate}
${inst.percentage}% = ₹${inst.amount}`;
    });

    summary += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Generated via Vyqda.com/tools`;
    
    navigator.clipboard.writeText(summary.trim());
    alert('✅ Advance tax schedule copied to clipboard!');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Input Section */}
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-2">📅</span> Advance Tax Calculator
        </h2>

        <div className="space-y-5">
          {/* Estimated Income */}
          <div>
            <label className="label">Estimated Annual Income</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={estimatedIncome}
                onChange={(e) => setEstimatedIncome(e.target.value)}
                placeholder="Enter estimated income"
                className="input-field pl-8"
                min="0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total income expected for the financial year
            </p>
          </div>

          {/* Estimated Tax */}
          <div>
            <label className="label">Estimated Tax Liability</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={estimatedTax}
                onChange={(e) => setEstimatedTax(e.target.value)}
                placeholder="Enter estimated tax"
                className="input-field pl-8"
                min="0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total tax liability including cess (use Income Tax Calculator)
            </p>
          </div>

          {/* TDS Deducted */}
          <div>
            <label className="label">TDS Already Deducted</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={tdsDeducted}
                onChange={(e) => setTdsDeducted(e.target.value)}
                placeholder="TDS on salary/other income"
                className="input-field pl-8"
                min="0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              TDS deducted by employer or others (Form 26AS)
            </p>
          </div>

          {/* Self Assessment Tax */}
          <div>
            <label className="label">Self Assessment Tax Paid</label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-gray-500 font-semibold">₹</span>
              <input
                type="number"
                value={selfAssessmentTax}
                onChange={(e) => setSelfAssessmentTax(e.target.value)}
                placeholder="Tax paid before filing ITR"
                className="input-field pl-8"
                min="0"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Any tax already paid through challan
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
              <span className="mr-2">ℹ️</span> Who Should Pay Advance Tax?
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Tax liability exceeds ₹10,000 in a year</li>
              <li>• Freelancers, consultants, business owners</li>
              <li>• Professionals with income other than salary</li>
              <li>• Anyone with capital gains, rental income</li>
              <li>• Senior citizens exempt (only dividend/business)</li>
            </ul>
          </div>

          {/* Penalty Info */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-4 border border-red-200">
            <h4 className="font-semibold text-red-900 mb-2 flex items-center">
              <span className="mr-2">⚠️</span> Non-Payment Consequences
            </h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• <strong>234B:</strong> 1% pm if tax not paid by Mar 31</li>
              <li>• <strong>234C:</strong> 1% pm for late installments</li>
              <li>• Interest calculated automatically by department</li>
              <li>• Cannot be avoided, must be paid</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="space-y-6">
        {results ? (
          <>
            <div className="card">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📊</span> Tax Liability Breakdown
              </h3>

              <div className="space-y-4">
                {/* Tax Summary */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tax Liability</span>
                    <span className="font-semibold">₹{results.totalTax}</span>
                  </div>
                  {parseFloat(results.tdsDeducted) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Less: TDS Deducted</span>
                      <span className="font-semibold">-₹{results.tdsDeducted}</span>
                    </div>
                  )}
                  {parseFloat(results.selfAssessmentTax) > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Less: Self Assessment Tax</span>
                      <span className="font-semibold">-₹{results.selfAssessmentTax}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 text-lg">
                    <span className="font-semibold">Advance Tax Payable</span>
                    <span className="font-bold text-primary-600">₹{results.advanceTaxLiability}</span>
                  </div>
                </div>

                {/* Applicability Check */}
                {results.isApplicable ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-semibold">
                      ✓ Advance tax is applicable (liability ≥ ₹10,000)
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 font-semibold">
                      ℹ️ Advance tax not applicable (liability &lt; ₹10,000)
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      You can pay remaining tax while filing ITR
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Schedule */}
            {results.isApplicable && (
              <div className="card">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📅</span> Payment Schedule
                </h3>

                <div className="space-y-3">
                  {results.installments.map((inst, index) => (
                    <div key={index} className="result-card">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-bold text-gray-900 text-lg">{inst.quarter}</div>
                          <div className="text-sm text-gray-600">Due: {inst.dueDate}</div>
                        </div>
                        <div className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full">
                          {inst.percentage}%
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-primary-200">
                        <span className="text-gray-600">Amount to Pay</span>
                        <span className="text-2xl font-bold text-primary-600">₹{inst.amount}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-2">
                        <span className="text-gray-500">Cumulative: {inst.cumulativePercentage}%</span>
                        <span className="font-semibold text-gray-700">₹{inst.cumulative}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="mt-4 result-card bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">Total Advance Tax</span>
                    <span className="text-3xl font-bold text-indigo-600">₹{results.advanceTaxLiability}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Interest Calculation */}
            {results.isApplicable && (
              <div className="card bg-gradient-to-br from-red-50 to-orange-50 border border-red-200">
                <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center">
                  <span className="mr-2">💸</span> Potential Interest on Non-Payment
                </h3>
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-700">Section 234B</div>
                        <div className="text-xs text-gray-500">If less than 90% paid by Mar 31</div>
                      </div>
                      <span className="text-xl font-bold text-red-600">₹{results.interest234B}</span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-700">Section 234C</div>
                        <div className="text-xs text-gray-500">On late/missed installments</div>
                      </div>
                      <span className="text-xl font-bold text-red-600">₹{results.interest234C}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-red-700 mt-3">
                  * These are estimated penalty amounts if advance tax is not paid on time
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="card">
              <h4 className="font-semibold text-gray-700 mb-3">Quick Actions</h4>
              <div className="flex flex-wrap gap-3">
                <button onClick={copyToClipboard} className="btn-primary flex items-center">
                  <span className="mr-2">📋</span> Copy Schedule
                </button>
                <button onClick={() => window.print()} className="btn-secondary flex items-center no-print">
                  <span className="mr-2">🖨️</span> Print
                </button>
                <a
                  href="https://www.tin-nsdl.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center"
                >
                  <span className="mr-2">💳</span> Pay Tax Online
                </a>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="card bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200">
              <h4 className="font-semibold text-cyan-900 mb-3 flex items-center">
                <span className="mr-2">📝</span> How to Pay Advance Tax?
              </h4>
              <ol className="text-sm text-cyan-800 space-y-2">
                <li className="flex">
                  <span className="font-bold mr-2">1.</span>
                  <span>Visit Income Tax e-Filing portal or TIN-NSDL website</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">2.</span>
                  <span>Select Challan 280 (Payment of Income Tax)</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">3.</span>
                  <span>Choose "Advance Tax (100)" as payment type</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">4.</span>
                  <span>Enter PAN and assessment year</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">5.</span>
                  <span>Make payment via netbanking/card</span>
                </li>
                <li className="flex">
                  <span className="font-bold mr-2">6.</span>
                  <span>Save challan counterfoil for ITR filing</span>
                </li>
              </ol>
            </div>

            {/* Pro Tips */}
            <div className="card bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                <span className="mr-2">💡</span> Pro Tips
              </h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Calculate tax liability accurately to avoid shortfall</li>
                <li>• Set reminders 2-3 days before each due date</li>
                <li>• Keep all challan receipts for ITR filing</li>
                <li>• Check Form 26AS to verify tax credit</li>
                <li>• Pay higher amount if income increases mid-year</li>
                <li>• For salaried: coordinate with employer TDS</li>
                <li>• Freelancers: estimate based on running income</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="card text-center py-12">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Ready to Plan Your Tax</h3>
            <p className="text-gray-500">Enter your income and tax details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceTaxCalculator;

